/* eslint-disable react/no-unescaped-entities */
import { Modal, IconButton } from "@mui/material"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"

import CloseIcon from "@mui/icons-material/Close"

import { ReactComponent as Notes } from "components/icons/svgs/notes.svg"
import "./ModalStoryShare.scss"
import { useMemo, useState } from "react"

const REACTIONS = [
  { id: 1, label: "Happy new year", emoji: "🎉", defaultActive: true },
  { id: 2, label: "Keep it up!", emoji: "💪" },
  { id: 3, label: "Don't touch me", emoji: "✋" },
  { id: 4, label: "Well done!", emoji: "👏" },
  { id: 5, label: "Great job today", emoji: "👍" },
  { id: 6, label: "Happy birthday", emoji: "🎂" },
  { id: 7, label: "TGIF", emoji: "🥂" },
  { id: 8, label: "Heartbroken", emoji: "💔" },
]

const MOODS = [
  { id: 1, emoji: "😄", name: "happy" },
  { id: 2, emoji: "😎", name: "cool" },
  { id: 3, emoji: "🤓", name: "nerd" },
  { id: 5, emoji: "😍", name: "love" },
  { id: 6, emoji: "🤪", name: "crazy" },
  { id: 10, emoji: "😊", name: "shy" },
  { id: 13, emoji: "🥳", name: "party" },
  { id: 15, emoji: "😜", name: "playful" },
  { id: 16, emoji: "🤔", name: "thinking" },
  { id: 18, emoji: "🙂", name: "slightly happy" },
  { id: 19, emoji: "😁", name: "very happy" },
  { id: 20, emoji: "😌", name: "chilling" },
  { id: 21, emoji: "🍀", name: "lucky" },
]

const ModalStoryShare = ({
  isOpen,
  onSubmit = () => {},
  onClose = () => {},
  date,
}) => {
  const defaultReactionId = useMemo(
    () => REACTIONS.find((r) => r.defaultActive)?.id ?? null,
    []
  )

  const [activeReactionId, setActiveReactionId] = useState(defaultReactionId)
  const [activeMoodId, setActiveMoodId] = useState(null)
  const [isSpinning, setIsSpinning] = useState(false)
  const [wheelRot, setWheelRot] = useState(0)

  const activeReaction = useMemo(
    () => REACTIONS.find((r) => r.id === activeReactionId) ?? null,
    [activeReactionId]
  )

  const activeMood = useMemo(
    () => MOODS.find((m) => m.id === activeMoodId) ?? null,
    [activeMoodId]
  )

  const payload = useMemo(
    () => ({
      reaction: activeReaction
        ? `${activeReaction.label} ${activeReaction.emoji ?? ""}`.trim()
        : null,
      reactionLabel: activeReaction?.label ?? null,
      reactionEmoji: activeReaction?.emoji ?? null,
      mood: activeMood ? activeMood.name : null,
      moodEmoji: activeMood?.emoji ?? null,
    }),
    [activeReaction, activeMood]
  )

  const spinMoodWheel = () => {
    if (isSpinning) return
    setIsSpinning(true)

    const n = REACTIONS.length
    const idx = Math.floor(Math.random() * n)
    const picked = REACTIONS[idx]

    // Each emoji occupies a slice
    const sliceSize = 360 / n

    // Emoji index i is positioned at angle: i * sliceSize
    // Pointer is at the top (12 o'clock position)
    // We need to rotate the wheel so that emoji[idx] aligns with pointer
    // Since emojis start at 0deg (3 o'clock), and pointer is at top (12 o'clock = -90deg or 270deg)
    // We need to rotate: -90 - (idx * sliceSize) to bring emoji to top
    const targetRotation = -90 - (idx + 0.5) * sliceSize

    // Add extra full rotations for animation effect
    const extraSpins = 6 + Math.floor(Math.random() * 2)

    // Calculate next rotation from current position
    const nextRot =
      wheelRot + extraSpins * 360 + (targetRotation - (wheelRot % 360))

    setWheelRot(nextRot)

    window.setTimeout(() => {
      setActiveReactionId(picked.id)
      setIsSpinning(false)
    }, 3200)
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="modal-story-share-container">
        <div className="modal-story-share-container-wrapper">
          <MDBox className="modal-story-share-header">
            <MDTypography
              className="modal-story-share-title"
              fontSize="16px"
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                padding: "16px",
              }}
            >
              Story setting
            </MDTypography>

            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </MDBox>

          <MDBox className="divider" />

          <div className="statusPanel">
            <section className="statusPanel__section">
              <h3 className="statusPanel__title">
                <Notes />
                What's on your mind?
              </h3>

              <div className="statusWheel">
                <div
                  className={`statusWheel__frame ${
                    isSpinning ? "isSpinning" : ""
                  }`}
                >
                  <div className="statusWheel__pointer" />

                  <div className="statusWheel__ring">
                    <div
                      className="statusWheel__wheel"
                      style={{
                        ["--n"]: String(REACTIONS.length),
                        ["--wheel-rot"]: `${wheelRot}deg`,
                      }}
                    >
                      {REACTIONS.map((m, i) => (
                        <div
                          key={m.id}
                          className="statusWheel__label"
                          style={{ ["--i"]: String(i) }}
                          title={m.label}
                        >
                          {m.label}
                        </div>
                      ))}

                      <div className="statusWheel__hub" />
                    </div>
                  </div>

                  <div className="statusWheel__gloss" />
                </div>
              </div>

              <div className="statusWheel__actions">
                <div className="statusWheel__result">
                  {activeReaction
                    ? `Result: ${activeReaction.label}`
                    : "Result: --"}
                </div>

                <button
                  type="button"
                  className="statusWheel__btn"
                  onClick={spinMoodWheel}
                  disabled={isSpinning}
                >
                  {isSpinning ? "Spinning..." : "Spin"}
                </button>

                <button
                  type="button"
                  className="statusWheel__btn statusWheel__btn--ghost"
                  onClick={() => {
                    setActiveReactionId(defaultReactionId)
                    onClose()
                  }}
                  disabled={isSpinning}
                >
                  Clear
                </button>
              </div>
            </section>

            <section className="statusPanel__section">
              <h3 className="statusPanel__title">Choose your emoji</h3>

              <div className="statusPanel__chips">
                {MOODS.map((item) => {
                  const isActive = item.id === activeMoodId
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`chip ${isActive ? "chip--active" : ""}`}
                      onClick={() => setActiveMoodId(item.id)}
                      aria-pressed={isActive}
                    >
                      {item.emoji}
                      <span>{item.name}</span>
                    </button>
                  )
                })}
              </div>
            </section>
          </div>

          <MDBox className="sticky-bot">
            <MDBox className="divider" />

            <MDBox
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              p={2}
              gap={2}
            >
              <MDBox
                className="modal-story-share-button cancel"
                onClick={onClose}
              >
                Cancel
              </MDBox>
              <MDBox
                className="modal-story-share-button submit"
                onClick={() => {
                  onSubmit(payload)
                  onClose()
                }}
              >
                Share
              </MDBox>
            </MDBox>
          </MDBox>
        </div>
      </div>
    </Modal>
  )
}

export default ModalStoryShare
